import React, { useCallback, useMemo } from 'react';
import { Button, Text } from 'components';
import { routes, TEXT_STRING } from '../../../../constants';
import { useMutation } from '@tanstack/react-query';
import { useToast } from 'hooks';
import {
  updateProjectOrderMaleApprovalStatus,
  confirmInvitedProjectOrder,
} from 'api';
import { useUserStore } from 'store';
import { useNavigate } from 'react-router-dom';
import { ApproveRejectMode, EStatus } from 'types';

const CALL_STR = TEXT_STRING.CALL_WOMAN;

type ConfirmAction = 'APPROVE' | 'REJECT' | null;

type Props = {
  firstPendingInvitedProjectOrderId: number;
  refetch: () => void;
  loading?: boolean;
  mode?: ApproveRejectMode;
};

function ApproveReject({
  firstPendingInvitedProjectOrderId,
  refetch,
  loading = false,
  mode = ApproveRejectMode.STEP1_PROJECT_INFO,
}: Props) {
  // Store
  const { isAuthenticated } = useUserStore();

  // Hook
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();

  const [confirmAction, setConfirmAction] = React.useState<ConfirmAction>(null);

  const isStep2 = mode === ApproveRejectMode.STEP2_AGREEMENT;

  const { mutate: mutateReject, isPending: isReject } = useMutation({
    mutationFn: () => {
      return updateProjectOrderMaleApprovalStatus({
        orderId: firstPendingInvitedProjectOrderId,
        maleApprovalStatus: EStatus.REJECTED,
      });
    },
    onSettled(_data, error) {
      toastSuccess(TEXT_STRING.CALL_WOMAN.UPDATE_REJECT_PROJECT_ORDER);
      refetch();
      if (error) {
        toastError(error);
      }
    },
  });

  const { mutate: mutateConfirmStep1, isPending: isConfirmingStep1 } =
    useMutation({
      mutationFn: () => {
        return confirmInvitedProjectOrder({
          orderId: firstPendingInvitedProjectOrderId,
        });
      },
      onSettled(_data, error) {
        refetch();
        toastSuccess(CALL_STR.UPDATE_APPROVE_PROJECT_ORDER_STEP1);
        if (error) toastError(error);
      },
    });

  const { mutate: mutateApproveStep2, isPending: isApprovingStep2 } =
    useMutation({
      mutationFn: () => {
        return updateProjectOrderMaleApprovalStatus({
          orderId: firstPendingInvitedProjectOrderId,
          maleApprovalStatus: EStatus.APPROVED,
        });
      },
      onSettled(_data, error) {
        refetch();
        toastSuccess(CALL_STR.UPDATE_APPROVE_PROJECT_ORDER_STEP2);
        if (error) toastError(error);
      },
    });
  const approveAction = useCallback(() => {
    if (!isAuthenticated) {
      navigate(routes.SIGNUP);
      return;
    }

    if (isStep2) {
      mutateApproveStep2();
      return;
    }

    mutateConfirmStep1();
  }, [
    isAuthenticated,
    isStep2,
    mutateApproveStep2,
    mutateConfirmStep1,
    navigate,
  ]);

  const approveLoading = useMemo(() => {
    return isStep2 ? isApprovingStep2 : isConfirmingStep1;
  }, [isStep2, isApprovingStep2, isConfirmingStep1]);

  const [confirmText, approveTitle, disabledCommon] = useMemo(() => {
    return [
      isStep2
        ? CALL_STR.CONFIRM_INVITED_PROJECT_ORDER_STEP2
        : CALL_STR.CONFIRM_INVITED_PROJECT_ORDER_STEP1,
      isStep2
        ? CALL_STR.APPROVE_PROJECT_ORDER_STEP2
        : CALL_STR.APPROVE_PROJECT_ORDER_STEP1,
      isReject || approveLoading || loading,
    ];
  }, [isStep2, isReject, approveLoading, loading]);

  const actions = React.useMemo<
    {
      title: string;
      action: () => void;
      bg: string;
      color?: `text-${string}`;
      border: string;
      disabled: boolean;
      loading: boolean;
    }[]
  >(
    () => [
      {
        title: CALL_STR.REJECT_PROJECT_ORDER,
        action: () => setConfirmAction('REJECT'),
        bg: 'bg-primary-50',
        color: 'text-primary',
        border: 'ring-1 ring-primary',
        disabled: disabledCommon,
        loading: isReject,
      },
      {
        title: approveTitle,
        action: () => {
          if (!isAuthenticated) {
            navigate(routes.SIGNUP);
            return;
          }
          setConfirmAction('APPROVE');
        },
        bg: 'bg-primary-50',
        color: 'text-primary',
        border: 'ring-1 ring-primary',
        disabled: disabledCommon,
        loading: approveLoading,
      },
    ],
    [
      approveTitle,
      isAuthenticated,
      isReject,
      approveLoading,
      navigate,
      disabledCommon,
    ],
  );


  return (
    <>
      <div className="px-4 mb-2">
        <Text className="p-4 rounded-xl bg-primary-50" center>
          {confirmText}
        </Text>
      </div>
      <div className="w-full bottom-0 flex gap-8px text-[14px] p-4 pt-0">
        {actions.map((item, index) => {
          return (
            <Button
              key={index}
              onClick={item.action}
              bgColor={item.bg}
              textColor={item.color}
              border={item.border}
              padding="py-7 flex-1"
              disabled={item.disabled || item.loading}
              loading={item.loading}
              loadingBorder="border-primary"
            >
              {item.title}
            </Button>
          );
        })}
      </div>

      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-[90%] max-w-[360px] rounded-2xl bg-white shadow-xl overflow-hidden animate-fade-in">
            <div className="px-6 py-5">
              <Text center bold className="text-[16px] mb-2">
                {confirmAction === 'APPROVE'
                  ? isStep2 ? CALL_STR.CONFIRM_MODAL_APPROVE_STEP2 : CALL_STR.CONFIRM_MODAL_APPROVE
                  : CALL_STR.CONFIRM_MODAL_REJECT}
              </Text>

              <Text
                center
                className="text-gray-500 text-[13px] leading-relaxed"
              >
                {confirmAction === 'APPROVE'
                  ? CALL_STR.CONFIRM_MODAL_APPROVE_DESC
                  : CALL_STR.CONFIRM_MODAL_REJECT_DESC}
              </Text>
            </div>

            <div className="h-[1px] bg-gray-100" />

            <div className="flex">
              <button
                className="flex-1 py-4 text-[14px] text-gray-500 hover:bg-gray-50 transition"
                onClick={() => setConfirmAction(null)}
              >
                {TEXT_STRING.COMMON.CANCEL}
              </button>

              <div className="w-[1px] bg-gray-100" />

              <button
                className={`flex-1 py-4 text-[14px] font-semibold transition
            ${confirmAction === 'APPROVE' ? 'bg-primary' : 'bg-red-400'}`}
                onClick={() => {
                  setConfirmAction(null);

                  if (confirmAction === 'REJECT') {
                    mutateReject();
                  } else {
                    approveAction();
                  }
                }}
              >
                {TEXT_STRING.COMMON.YES}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ApproveReject;
